import React, { ChangeEventHandler, useContext, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as UserPicture } from '../../../../../assets/icons/user-picture-ne.svg';
import StyledButtonNE from '../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import { EDIT_NE, TRASH_NE } from '../../../../../shared/constants/images';
import { AVATAR_IMAGE_UPLOADER_VALIDATORS } from '../../../../../shared/constants/settings';
import {
  CLICK_ACTION,
  DELETE_PICTURE,
  SECTION_CONFIGURATION,
  UPLOAD_PICTURE
} from '../../../../../shared/constants/tagManager';
import { UPDATE_USER_IMAGE_ERROR_TOAST, UPDATE_USER_IMAGE_SUCCESS_TOAST } from '../../../../../shared/constants/toast';
import { SessionContext } from '../../../../../shared/context/auth/authContext';
import { IToast } from '../../../../../shared/interfaces/toast';
import Users from '../../../../../shared/services/api/users/users';
import tagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_SETTINGS } from '../../../../../shared/translations/settings';
import { fileToBase64, validateFileList } from '../../../../../shared/utils/utils';

import './Avatar.scss';

interface IAvatarProps {
  handleOpenToast: (toast: IToast) => void;
}

const Avatar = ({ handleOpenToast }: IAvatarProps) => {
  const [loadingChange, setLoadingChange] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { user, setUserAttributes } = useContext(SessionContext);
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const clearFileInput = () => {
    if (fileInput?.current) {
      fileInput.current.value = '';
    }
  };

  const onImageChange: ChangeEventHandler<HTMLInputElement> = async () => {
    if (fileInput.current?.files) {
      const { invalidCount, invalidFormat, invalidSize } = validateFileList(
        fileInput.current.files,
        AVATAR_IMAGE_UPLOADER_VALIDATORS.maxFileCount,
        AVATAR_IMAGE_UPLOADER_VALIDATORS.fileFormats,
        AVATAR_IMAGE_UPLOADER_VALIDATORS.maxFileSize
      );
      if (!invalidCount && !invalidFormat.length && !invalidSize) {
        const file = fileInput.current.files[0];
        const base64 = await fileToBase64(file);
        await updateImage(base64);
      } else {
        let errorText = TRANSLATIONS_SETTINGS.error.image.generic;
        if (invalidSize) {
          errorText = TRANSLATIONS_SETTINGS.error.image.size;
        } else if (invalidFormat) {
          errorText = TRANSLATIONS_SETTINGS.error.image.format;
        }
        handleOpenToast({ ...UPDATE_USER_IMAGE_ERROR_TOAST, description: errorText });
        clearFileInput();
      }
    }
  };

  const updateImage = async (base64: string) => {
    try {
      setLoadingChange(true);
      const { url } = await Users.putUserImage(user.email, base64);
      setUserAttributes({ image: url });
      handleOpenToast(UPDATE_USER_IMAGE_SUCCESS_TOAST);
    } catch (error) {
      handleOpenToast(UPDATE_USER_IMAGE_ERROR_TOAST);
    } finally {
      setLoadingChange(false);
      clearFileInput();
    }
  };

  const editImage = () => {
    tagManagerService.pushEvent(CLICK_ACTION, UPLOAD_PICTURE, SECTION_CONFIGURATION);
    fileInput.current?.click();
  };

  const deleteImage = async () => {
    tagManagerService.pushEvent(CLICK_ACTION, DELETE_PICTURE, SECTION_CONFIGURATION);
    try {
      setLoadingDelete(true);
      clearFileInput();
      await Users.deleteUserImage(user.email);
      setUserAttributes({ image: '' });
    } catch (error) {
      handleOpenToast(UPDATE_USER_IMAGE_ERROR_TOAST);
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <div className="avatar-card-ne">
      <input
        className="d-none"
        ref={fileInput}
        type="file"
        onChange={onImageChange}
        accept="image/png, image/jpeg, image/jpg"
        data-testid="avatar-file-input"
      />
      <div className="avatar-image" style={{ backgroundImage: `url("${user.image}")` }}>
        {!user.image && <UserPicture className="" />}
      </div>
      <div className="avatar-ne-tablet-container">
        <div className="avatar-name hide-desktop">{`${user.name} ${user.lastName}`}</div>
        <div className="avatar-buttons">
          {user.image ? (
            <>
              <StyledButtonNE
                buttonType="secondary"
                image={{ ...EDIT_NE, position: 'left' }}
                action={editImage}
                loading={loadingChange}
              >
                {t(TRANSLATIONS_SETTINGS.avatar.edit)}
              </StyledButtonNE>
              <div className="only-desktop">
                <StyledButtonNE
                  buttonType="secondary"
                  image={{ ...TRASH_NE, position: 'left' }}
                  action={deleteImage}
                  loading={loadingDelete}
                >
                  {t(TRANSLATIONS_SETTINGS.avatar.delete)}
                </StyledButtonNE>
              </div>
            </>
          ) : (
            <StyledButtonNE
              buttonType="secondary"
              image={{ ...EDIT_NE, position: 'left' }}
              action={editImage}
              loading={loadingChange}
            >
              {t(TRANSLATIONS_SETTINGS.avatar.upload)}
            </StyledButtonNE>
          )}
        </div>
      </div>
      <div className="avatar-name only-desktop">{`${user.name} ${user.lastName}`}</div>
    </div>
  );
};

export default Avatar;
